import axios from 'axios';
import { get } from 'lodash';
import { KEYCLOAK_VERIFY } from '../../constants/endpoints';
const authenticatedLanding = '/app/dashboard';

const KEYCLOAK_CONFIG = {
  CLIENT_ID: process.env.REACT_APP_KEYCLOAK_CLIENT_ID,
  BASE_URL: process.env.REACT_APP_KEYCLOAK_URL,
  REALM: process.env.REACT_APP_KEYCLOAK_REALM,
  CLIENT_SECRET: process.env.REACT_APP_KEYCLOAK_CLIENT_SECRET
};

export const redirectToKeycloak = () => {
  const redirectUri = encodeURIComponent(`${window.location.origin}/auth/callback`);
  const idpHint = process.env.REACT_APP_KEYCLOAK_IDP_HINT;

  const authUrl = `${KEYCLOAK_CONFIG.BASE_URL}/realms/${KEYCLOAK_CONFIG.REALM}/protocol/openid-connect/auth?response_type=code&client_id=${KEYCLOAK_CONFIG.CLIENT_ID}&redirect_uri=${redirectUri}&kc_idp_hint=${idpHint}&scope=openid profile email`;

  window.location.replace(authUrl);
};

export const exchangeCodeForTokens = async code => {
  const data = new URLSearchParams();
  data.append('grant_type', 'authorization_code');
  data.append('client_id', KEYCLOAK_CONFIG.CLIENT_ID);
  data.append('client_secret', KEYCLOAK_CONFIG.CLIENT_SECRET);
  data.append('code', code);
  data.append('redirect_uri', `${window.location.origin}/auth/callback`);

  const tokenUrl = `${KEYCLOAK_CONFIG.BASE_URL}/realms/${KEYCLOAK_CONFIG.REALM}/protocol/openid-connect/token`;

  try {
    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: data.toString()
    });

    const result = await response.json();

    if (result.access_token) {
      sessionStorage.setItem(SESSION_KEYS.ACCESS_TOKEN, result.access_token);
      sessionStorage.setItem(SESSION_KEYS.REFRESH_TOKEN, result.refresh_token);
      sessionStorage.setItem(SESSION_KEYS.ID_TOKEN, result.id_token);
      return result;
    } else {
      throw new Error(`Token exchange failed: ${JSON.stringify(result)}`);
    }
  } catch (error) {
    throw error;
  }
};

export const syncTokenStorages = reduxToken => {
  const sessionToken = sessionStorage.getItem(SESSION_KEYS.ACCESS_TOKEN);

  if (reduxToken && !sessionToken) {
    sessionStorage.setItem(SESSION_KEYS.ACCESS_TOKEN, reduxToken);
    return 'SYNCED_TO_SESSION';
  }

  if (!reduxToken && sessionToken) {
    sessionStorage.removeItem(SESSION_KEYS.ACCESS_TOKEN);
    return 'CLEARED_SESSION';
  }

  if (reduxToken && sessionToken) {
    if (reduxToken === sessionToken) {
      return 'ALREADY_SYNCED';
    } else {
      sessionStorage.setItem(SESSION_KEYS.ACCESS_TOKEN, reduxToken);
      return 'MISMATCH_FIXED';
    }
  }

  return 'NO_TOKENS';
};

// ==================== Auth method cache ====================
let authMethodCache = {
  method: null,
  tokenHash: null
};

const simpleHash = str => {
  if (!str) return null;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return hash.toString();
};

export const isUsingKeycloakAuth = () => {
  const sessionToken = sessionStorage.getItem(SESSION_KEYS.ACCESS_TOKEN);
  if (!sessionToken) {
    authMethodCache = { method: 'jwt', tokenHash: null };
    return false;
  }

  const currentTokenHash = simpleHash(sessionToken);
  if (authMethodCache.tokenHash === currentTokenHash && authMethodCache.method) {
    return authMethodCache.method === 'keycloak';
  }

  try {
    const payload = JSON.parse(atob(sessionToken.split('.')[1]));
    const isKeycloak = !!(
      payload.iss &&
      payload.iss.includes(KEYCLOAK_CONFIG.BASE_URL) &&
      payload.azp === KEYCLOAK_CONFIG.CLIENT_ID
    );

    authMethodCache = {
      method: isKeycloak ? 'keycloak' : 'jwt',
      tokenHash: currentTokenHash
    };

    return isKeycloak;
  } catch (error) {
    authMethodCache = { method: 'jwt', tokenHash: currentTokenHash };
    return false;
  }
};

export const getCurrentAuthMethod = () => {
  return isUsingKeycloakAuth() ? 'keycloak' : 'jwt';
};

export const clearAuthMethodCache = () => {
  authMethodCache = { method: null, tokenHash: null };
};

export const isProfileIncompleteError = (error) => {
  const errorMessage = get(error, 'response.data.message');
  return errorMessage && (errorMessage.includes('profile_incomplete') || errorMessage.includes('profile incomplete'));
};

// ==================== Profile incomplete flag ====================

export const setProfileIncompleteError = () => {
  sessionStorage.setItem('profile_incomplete_active', 'true');
};

export const hasProfileIncompleteError = () => {
  return sessionStorage.getItem('profile_incomplete_active') === 'true';
};

export const clearProfileIncompleteError = () => {
  sessionStorage.removeItem('profile_incomplete_active');
};

export const clearAllTokens = () => {
  sessionStorage.removeItem(SESSION_KEYS.ACCESS_TOKEN);
  sessionStorage.removeItem(SESSION_KEYS.REFRESH_TOKEN);
  sessionStorage.removeItem(SESSION_KEYS.ID_TOKEN);
  clearAuthMethodCache();
};

export { KEYCLOAK_CONFIG };

export const SESSION_KEYS = {
  ACCESS_TOKEN: 'app_session_key',
  ID_TOKEN: 'id_token',
  REFRESH_TOKEN: 'refresh_token'
};

export const shouldUseKeycloakLogout = () => {
  return isUsingKeycloakAuth();
};

export const getKeycloakLogoutUrl = () => {
  const idToken = sessionStorage.getItem('id_token');
  const redirectUri = encodeURIComponent(`${window.location.origin}/logout-success`);

  let logoutUrl = `${KEYCLOAK_CONFIG.BASE_URL}/realms/${KEYCLOAK_CONFIG.REALM}/protocol/openid-connect/logout`;
  logoutUrl += `?id_token_hint=${idToken}`;
  logoutUrl += `&post_logout_redirect_uri=${redirectUri}`;

  return logoutUrl;
};

export const verifyKeycloakToken = async (token) => {
  if (!token) throw new Error('No token provided for verification');

  try {
    await axios.post(KEYCLOAK_VERIFY, {}, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-AUTH-METHOD': 'keycloak'
      }
    });
    return true;
  } catch (error) {
    throw error;
  }
};

export const shouldRedirectToCobranza = (stateParam) => {
  if (!stateParam) return false;

  try {
    const decodedState = atob(stateParam);
    const stateData = JSON.parse(decodedState);
    return stateData.porcobrar === true;
  } catch (error) {
    console.error('Error decoding state parameter:', error);
    return false;
  }
};


// SSO-Redirect routes
export const ROLE_ROUTES = {
  1: authenticatedLanding,  
  2: authenticatedLanding,
};


// Default route for unknown roles (cobranza call-center flow)
//
// SECURITY FIX H-017 (JWT expuesto en parametro de URL): this used to build
// `/cobranza/?tokencallcenter=<JWT_COMPLETO>`, leaking the full access token into
// browser history, server access logs, intermediate proxies, and the Referer
// header. The token now travels via sessionStorage (same SESSION_KEYS mechanism
// already used for the Keycloak session token, see syncTokenStorages() above) and
// the URL only carries a non-secret flow marker (`tokencallcenter=1`) so
// paymentPortal/index.js knows which validation branch to take. The actual JWT
// reaches the gateway via the standard `Authorization: Bearer <token>` header,
// already attached automatically to every axios request by the interceptor in
// src/app/intercept.js:54 (`config.headers['Authorization'] = `Bearer ${access_token}`;`),
// which the gateway's extractToken() already supports as a fallback to the body
// field (see por-cobrar-gateway-sura/src/utils/authFunc.ts:9-17) — no backend change
// needed.
export const DEFAULT_ROUTE = (accessToken) => {
  if (accessToken) {
    sessionStorage.setItem(SESSION_KEYS.ACCESS_TOKEN, accessToken);
  }
  return '/cobranza/?tokencallcenter=1';
};


export const getRouteForRole = (rolId, accessToken) => {
  const route = ROLE_ROUTES[rolId];
  
  // If route is a function, call it with the access token
  if (typeof route === 'function') {
    return route(accessToken);
  }
  
  // If route exists and is a string, return it
  if (route) {
    return route;
  }
  
  // Otherwise, return default route
  // If default route is a function, call it with the access token
  if (typeof DEFAULT_ROUTE === 'function') {
    return DEFAULT_ROUTE(accessToken);
  }
  
  return DEFAULT_ROUTE;
};


// =============================================================================
// REFRESH TOKEN FUNCTIONS (COMMENTED OUT)
// =============================================================================
// NOTE: These functions are currently not in use because both access_token
// and refresh_token have the same expiration time in our Keycloak setup.
// Following the original architecture diagram, we use redirect-to-login
// approach instead of automatic token refresh.
// Keep these functions for future use if token lifespans are configured
// differently (e.g., longer refresh_token lifespan).
// =============================================================================

/**
 * Checks if the access token is expired or about to expire
 */
// export const isTokenExpired = (token, bufferSeconds = 30) => {
//   if (!token) return true;

//   try {
//     const payload = JSON.parse(atob(token.split('.')[1]));
//     const now = Math.floor(Date.now() / 1000);

//     // Check if token expires within buffer time
//     return payload.exp - bufferSeconds <= now;
//   } catch (error) {
//     console.warn('Error checking token expiration:', error);
//     return true;
//   }
// };

/**
 * Refreshes the access token using the refresh token
 * Makes a request to Keycloak token endpoint with refresh_token grant
 */
// export const refreshAccessToken = async () => {
//   const refreshToken = sessionStorage.getItem(SESSION_KEYS.REFRESH_TOKEN);

//   if (!refreshToken) {
//     throw new Error('No refresh token available');
//   }

//   const data = new URLSearchParams();
//   data.append('grant_type', 'refresh_token');
//   data.append('client_id', KEYCLOAK_CONFIG.CLIENT_ID);
//   data.append('refresh_token', refreshToken);

//   const tokenUrl = `${KEYCLOAK_CONFIG.BASE_URL}/realms/${KEYCLOAK_CONFIG.REALM}/protocol/openid-connect/token`;

//   try {
//     const response = await fetch(tokenUrl, {
//       method: 'POST',
//       headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
//       body: data.toString()
//     });

//     if (!response.ok) {
//       throw new Error(`Token refresh failed: ${response.status}`);
//     }

//     const result = await response.json();

//     if (result.access_token) {
//       // Update tokens in storage
//       sessionStorage.setItem(SESSION_KEYS.ACCESS_TOKEN, result.access_token);

//       // Refresh token might be renewed too
//       if (result.refresh_token) {
//         sessionStorage.setItem(SESSION_KEYS.REFRESH_TOKEN, result.refresh_token);
//       }

//       // Clear auth method cache to force re-detection
//       clearAuthMethodCache();

//       console.log('🔄 [Auth] Token refreshed successfully');
//       return result.access_token;
//     } else {
//       throw new Error(`Invalid refresh response: ${JSON.stringify(result)}`);
//     }
//   } catch (error) {
//     console.error('🔄 [Auth] Token refresh failed:', error);

//     // Clear all tokens if refresh fails
//     clearAllTokens();
//     throw error;
//   }
// };

/**
 * Ensures we have a valid access token, refreshing if needed
 */
// export const ensureValidToken = async () => {
//   const accessToken = sessionStorage.getItem(SESSION_KEYS.ACCESS_TOKEN);

//   if (!accessToken) {
//     throw new Error('No access token available');
//   }

//   if (isTokenExpired(accessToken)) {
//     console.log('🔄 [Auth] Token expired, refreshing...');
//     return await refreshAccessToken();
//   }

//   return accessToken;
// };



// WEBPACK FOOTER //
// ./src/components/auth/keycloakUtils.js